const API_BASE = import.meta.env.VITE_API_URL || "";

/** Stored acting-as user ID for secretary delegation */
let _actingAsUserId: number | null = null;

export function setActingAsUserId(userId: number | null) {
  _actingAsUserId = userId;
}

export function getActingAsUserId(): number | null {
  return _actingAsUserId;
}

type RequestOptions = {
  method?: string;
  body?: any;
  headers?: Record<string, string>;
};

async function request<T = any>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, headers = {} } = opts;

  const config: RequestInit = {
    method,
    credentials: "include",
    headers: {
      ...headers,
    },
  };

  // Add acting-as header for secretary delegation
  if (_actingAsUserId) {
    (config.headers as Record<string, string>)["X-Acting-As"] = String(_actingAsUserId);
  }

  if (body && !(body instanceof FormData)) {
    config.headers = { ...config.headers, "Content-Type": "application/json" };
    config.body = JSON.stringify(body);
  } else if (body instanceof FormData) {
    config.body = body;
  }

  const resp = await fetch(`${API_BASE}${path}`, config);

  if (!resp.ok) {
    const errorData = await resp.json().catch(() => ({ error: resp.statusText }));
    throw new Error((errorData as any).error || `Request failed: ${resp.status}`);
  }

  // Handle text responses (iCal, etc.)
  const contentType = resp.headers.get("Content-Type") || "";
  if (contentType.includes("text/")) {
    return (await resp.text()) as unknown as T;
  }

  return resp.json();
}

export const api = {
  get: <T = any>(path: string) => request<T>(path),
  post: <T = any>(path: string, body?: any) => request<T>(path, { method: "POST", body }),
  put: <T = any>(path: string, body?: any) => request<T>(path, { method: "PUT", body }),
  patch: <T = any>(path: string, body?: any) => request<T>(path, { method: "PATCH", body }),
  delete: <T = any>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T = any>(path: string, formData: FormData) => request<T>(path, { method: "POST", body: formData }),
};
